import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  SearchOutlined,
  ArrowRightOutlined,
  FireOutlined,
  AppstoreOutlined,
  TeamOutlined,
  BankOutlined,
  ReadOutlined,
  HomeOutlined,
  TrophyOutlined,
  ShopOutlined,
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import Header from "../../components/Header";
import "./style.css";

// 标签配置 — 蓝绿紫色系（参考首页，降低饱和度）
const TAB_CONFIG = [
  { key: "秒应核心功能", label: "秒应核心功能", icon: <AppstoreOutlined />, color: "#3b82f6" },
  { key: "老师班级管理", label: "老师班级管理", icon: <ReadOutlined />, color: "#7c6fae" },
  { key: "行政企业管理", label: "行政企业管理", icon: <BankOutlined />, color: "#0d9488" },
  { key: "大学班委统计", label: "大学班委统计", icon: <TeamOutlined />, color: "#e11d48" },
  { key: "社区生活统计", label: "社区生活统计", icon: <HomeOutlined />, color: "#ea580c" },
  { key: "团队活动组织", label: "团队活动组织", icon: <TrophyOutlined />, color: "#16a34a" },
  { key: "商铺社区团购", label: "商铺社区团购", icon: <ShopOutlined />, color: "#ca8a04" },
  { key: "机构预约报名", label: "机构预约报名", icon: <CalendarOutlined />, color: "#db2777" },
];

// 每页显示条数
const PAGE_SIZE = 6;

const HelpCenter = () => {
  const [activeTab, setActiveTab] = useState(TAB_CONFIG[0].key);
  const [articles, setArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const cacheRef = useRef({});

  // 从 md 文件加载文章列表
  const fetchArticles = useCallback(async (tabKey) => {
    if (cacheRef.current[tabKey]) {
      setArticles(cacheRef.current[tabKey]);
      return;
    }
    setLoading(true);
    try {
      const indexPath = `/scenarios/${tabKey}/index.json`;
      let fileList = [];
      try {
        const res = await fetch(indexPath);
        if (res.ok) fileList = await res.json();
      } catch { fileList = []; }

      if (fileList.length === 0) {
        const mappingRes = await fetch("/scenarios/mapping.json");
        if (mappingRes.ok) {
          const mapping = await mappingRes.json();
          fileList = mapping[tabKey] || [];
        }
      }

      const articleList = [];
      for (const file of fileList) {
        try {
          const mdRes = await fetch(`/scenarios/${tabKey}/${file}`);
          if (mdRes.ok) {
            const mdText = await mdRes.text();
            let title = "";
            const fmMatch = mdText.match(/^---\s*\n([\s\S]*?)\n---/);
            if (fmMatch) {
              const titleMatch = fmMatch[1].match(/^title:\s*(.+)$/m);
              if (titleMatch) title = titleMatch[1].trim();
            }
            if (!title) {
              const h1Match = mdText.match(/^#\s+(.+)$/m);
              if (h1Match) title = h1Match[1].trim();
            }
            // 如果仍无标题，取第一行非空文本
            if (!title) {
              const firstLine = mdText.split('\n').map(l => l.trim()).find(l => l.length > 0);
              if (firstLine) title = firstLine.replace(/^#+\s*/, '');
            }
            if (title) {
              articleList.push({ key: `${tabKey}/${file}`, title, file, category: tabKey });
            }
          }
        } catch { /* ignore */ }
      }

      cacheRef.current[tabKey] = articleList;
      setArticles(articleList);
    } catch (error) {
      console.error("加载文章列表失败:", error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredArticles = searchQuery
    ? articles.filter((a) => a.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : articles;

  const currentTab = TAB_CONFIG.find((t) => t.key === activeTab) || TAB_CONFIG[0];
  const totalPages = Math.max(1, Math.ceil(filteredArticles.length / PAGE_SIZE));
  const pagedArticles = filteredArticles.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => { setCurrentPage(1); }, [activeTab]);
  useEffect(() => { setIsLoaded(true); }, []);
  useEffect(() => { fetchArticles(activeTab); }, [activeTab, fetchArticles]);

  return (
    <div className={`helpcenter-page ${isLoaded ? "loaded" : ""}`}>
      <Header activePath="/helpcenter" />

      {/* 顶部搜索横幅 */}
      <section className="hc-banner">
        <div className="hc-banner-decor hc-decor-1" />
        <div className="hc-banner-decor hc-decor-2" />
        <div className="hc-banner-decor hc-decor-3" />
        <div className="hc-banner-inner">
          <div className="hc-banner-info">
            <h1 className="hc-banner-title">秒应使用场景技巧</h1>
            <p className="hc-banner-desc">
              秒应是一款专为社群管理、活动组织、信息收集设计的智能工具
              <br />
              帮助老师、班委、行政人员等高效完成各类统计与通知工作
            </p>
          </div>
          <div className="hc-banner-search">
            <div className="hc-search">
              <SearchOutlined className="hc-search-icon" />
              <input
                type="text"
                className="hc-search-input"
                placeholder="搜索使用技巧..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="hc-search-clear" onClick={() => setSearchQuery("")}>×</button>
              )}
              <button className="hc-search-btn">搜索</button>
            </div>
          </div>
        </div>
      </section>

      {/* 场景分类卡片 */}
      <section className="hc-categories">
        <div className="hc-categories-inner">
          <div className="hc-cat-grid">
            {TAB_CONFIG.map((tab) => (
              <button
                key={tab.key}
                className={`hc-cat-card ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => { setActiveTab(tab.key); setSearchQuery(""); }}
                style={{ '--cat-color': tab.color }}
              >
                <div className="hc-cat-icon-wrap">
                  <span className="hc-cat-icon" style={{ color: activeTab === tab.key ? '#fff' : tab.color }}>{tab.icon}</span>
                </div>
                <span className="hc-cat-name">{tab.label}</span>
                <ArrowRightOutlined className="hc-cat-arrow" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 文章列表 + 分页 */}
      <section className="hc-main-content">
        <div className="hc-main-inner">
          <div className="hc-articles-header">
            <div className="hc-articles-header-left">
              <span className="hc-articles-dot" style={{ background: currentTab.color }} />
              <h2 className="hc-articles-title">
                {searchQuery ? `搜索「${searchQuery}」` : currentTab.label}
              </h2>
            </div>
            <span className="hc-articles-count">{filteredArticles.length} 篇文章</span>
          </div>

          <div className="hc-articles-body">
            {loading ? (
              <div className="hc-loading">
                <div className="hc-spinner" />
                <span>加载中...</span>
              </div>
            ) : filteredArticles.length > 0 ? (
              <div className="hc-article-list">
                {pagedArticles.map((article, index) => (
                  <Link
                    key={article.key}
                    to={`/helpcenter/${encodeURIComponent(article.category)}/${encodeURIComponent(article.file.replace('.md', ''))}`}
                    className="hc-article-item"
                    style={{ '--accent': currentTab.color, animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="hc-article-num" style={{ color: currentTab.color }}>
                      {String((currentPage - 1) * PAGE_SIZE + index + 1).padStart(2, '0')}
                    </div>
                    <div className="hc-article-content">
                      <h3 className="hc-article-title">{article.title}</h3>
                    </div>
                    <ArrowRightOutlined className="hc-article-arrow" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="hc-empty">
                <div className="hc-empty-icon">📖</div>
                <p>{searchQuery ? "未找到相关文章" : "该分类暂无文章"}</p>
              </div>
            )}
          </div>

          {!loading && filteredArticles.length > 0 && (
            <div className="hc-pagination">
              <button
                className="hc-page-btn"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <LeftOutlined /> 上一页
              </button>
              <span className="hc-page-info">第 {currentPage} / {totalPages} 页</span>
              <button
                className="hc-page-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                下一页 <RightOutlined />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="hc-cta">
        <div className="hc-cta-inner">
          <h2 className="hc-cta-title">现在就试试秒应</h2>
          <p className="hc-cta-desc">扫码立即体验，免费使用，无需注册</p>
          <div className="hc-cta-qr-codes">
            <div className="hc-qr-item">
              <img src="/assets/code1.8137c5d.jpg" alt="微信小程序码" className="hc-qr-img" />
              <span className="hc-qr-label">微信小程序</span>
            </div>
            <div className="hc-qr-item">
              <img src="/assets/code4.6658546.jpg" alt="网页版二维码" className="hc-qr-img" />
              <span className="hc-qr-label">微信公众号</span>
            </div>
            <div className="hc-qr-item">
              <img src="/assets/code3.563ecfb.jpg" alt="客服微信" className="hc-qr-img" />
              <span className="hc-qr-label">客服微信：jizhitech</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenter;
